import React, { useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { DynamicTable, TableColumn } from '../../../../../components/Table';

import { BillingPaymentService } from '../../Services';
import { DataGetBillingInformationResponse } from '../../Services/Interface';
import { BillingPaymentsContext } from '../../Context';

const useRequest = () => useContext(BillingPaymentsContext);

const TableBillingScreen: React.FC = () => {
  const { t, i18n } = useTranslation();
  const [isLoading, setIsLoading] = useState(false);

  const [responseItemsPerPage, setResponseItemsPerPage] = useState(0);
  const [currentPageResponse, setCurrentPageResponse] = useState(0);
  const [totalItems, setTotalItems] = useState(0);
  const [toNextPage, setToNextPage] = useState(0);
  const [sortByValue, setSortByValue] = useState<string>('');
  const [sortDirectionValue, setSortDirectionValue] = useState<string>('asc');

  const [billingInformation, setBillingInformation] = useState<DataGetBillingInformationResponse[] | []>([]);

  const { config } = useRequest();

  const lastFetchParamsRef = useRef<string | null>(null);
  const isFetchingRef = useRef(false);

  const columns = useMemo(
    () =>
      [
        { columnKey: 'id', header: 'ID' },
        { columnKey: 'description', header: t('billingPayments.billingPaymentsDescription') },
        { columnKey: 'amount', header: t('billingPayments.billingPaymentsAmount') },
        { columnKey: 'date', header: t('billingPayments.billingPaymentsDate') },
        { columnKey: 'payment_method', header: t('billingPayments.billingPaymentsPaymentMethod') },
        { columnKey: 'status', header: t('billingPayments.billingPaymentsStatus') },
        { columnKey: 'type', header: t('billingPayments.billingPaymentsType') }
      ] as TableColumn<any>[],
    [t, i18n.language]
  );

  const fetchGetBillingInformation = useCallback(
    async (page: number, sortBy: string, sortDirection: string, force = false) => {
      if (!config || isFetchingRef.current) {
        return;
      }

      const paramsKey = `${page}|${sortBy}|${sortDirection}`;
      if (!force && lastFetchParamsRef.current === paramsKey) {
        return;
      }

      lastFetchParamsRef.current = paramsKey;
      isFetchingRef.current = true;
      setIsLoading(true);
      try {
        const { data } = await BillingPaymentService.getBillingInformation(config, page, sortBy, sortDirection);

        setBillingInformation(data.data);
        setResponseItemsPerPage(data.per_page || 0);
        setCurrentPageResponse(data.current_page);
        setTotalItems(data.total || 0);
        setToNextPage(data.to || 0);
      } catch (error) {
        lastFetchParamsRef.current = null;
        console.error('Error Get Billing Information', error);
      } finally {
        setIsLoading(false);
        isFetchingRef.current = false;
      }
    },
    [config]
  );

  const hasFetchedInitialRef = useRef(false);

  useEffect(() => {
    if (!config || hasFetchedInitialRef.current) {
      return;
    }

    hasFetchedInitialRef.current = true;
    void fetchGetBillingInformation(currentPageResponse, sortByValue, sortDirectionValue, true);
  }, [config, currentPageResponse, fetchGetBillingInformation, sortByValue, sortDirectionValue]);

  return (
    <div className="rounded-lg border-[2px] border-solid border-[#E1E9EF] bg-white">
      {isLoading ? (
        <div className="flex min-h-[400px] flex-col items-center justify-center gap-3 py-12">
          <div className="h-8 w-8 animate-spin rounded-full border-4 border-[#E1E9EF] border-t-[#0C1FAA]"></div>
          <span className="text-base text-[#727272]">{t('OnboardingRequestLoadingData')}</span>
        </div>
      ) : (
        <div className="overflow-hidden">
          <DynamicTable
            data={billingInformation}
            columns={columns}
            itemsPerPage={responseItemsPerPage}
            currentPage={currentPageResponse}
            totalItems={totalItems}
            toNextPage={toNextPage}
            onPageChange={(page, sortBy, sortDirection) => {
              const normalizedSortBy = sortBy ?? '';
              const normalizedSortDirection = sortDirection ?? 'asc';
              setSortByValue(normalizedSortBy);
              setSortDirectionValue(normalizedSortDirection);
              setCurrentPageResponse(page);
              void fetchGetBillingInformation(page, normalizedSortBy, normalizedSortDirection, true);
            }}
            sortBy={sortByValue}
            sortDirection={sortDirectionValue}
          />
        </div>
      )}
    </div>
  );
};

export { TableBillingScreen };
